"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Editorial() {
  const container = useRef<HTMLElement>(null);
  const headlineWords = [{
    text: "I",
    italic: false,
    marked: false
  }, {
    text: "believe",
    italic: false,
    marked: false
  }, {
    text: "SEO",
    italic: false,
    marked: false
  }, {
    text: "should",
    italic: false,
    marked: false
  }, {
    text: "be",
    italic: false,
    marked: false
  }, {
    text: "transparent.",
    italic: true,
    marked: true
  }, {
    text: "It",
    italic: false,
    marked: false
  }, {
    text: "drives",
    italic: false,
    marked: false
  }, {
    text: "real",
    italic: false,
    marked: false
  }, {
    text: "growth",
    italic: false,
    marked: false
  }, {
    text: "without",
    italic: false,
    marked: false
  }, {
    text: "relying",
    italic: false,
    marked: false
  }, {
    text: "on",
    italic: false,
    marked: false
  }, {
    text: "shortcuts.",
    italic: false,
    marked: false
  }];
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      reduced: "(prefers-reduced-motion: reduce)",
      normal: "(prefers-reduced-motion: no-preference)"
    }, context => {
      const conditions = context.conditions as {
        reduced: boolean;
        normal: boolean;
      };
      if (conditions.reduced) {
        gsap.set([".ed-word", ".ed-body", ".ed-signature-block", ".ed-marker-path", ".ed-divider-path"], {
          opacity: 1,
          y: 0
        });
        gsap.set(".ed-marker-path", {
          strokeDashoffset: 0
        });
        gsap.set(".ed-divider-path", {
          strokeDashoffset: 0
        });
        return;
      }
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top 80%",
          once: true
        }
      });
      tl.from(".ed-word", {
        y: 40,
        opacity: 0,
        duration: 0.9,
        stagger: 0.06,
        ease: "cubic-bezier(0.22, 1, 0.36, 1)"
      }).from(".ed-marker-path", {
        strokeDashoffset: (i, el) => (el as SVGPathElement).getTotalLength(),
        duration: 0.7,
        ease: "power2.out"
      }, "-=0.2").from([".ed-body", ".ed-signature-block"], {
        y: 24,
        opacity: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: "power3.out"
      }, "-=0.3").from(".ed-divider-path", {
        strokeDashoffset: (i, el) => (el as SVGPathElement).getTotalLength(),
        duration: 0.8,
        ease: "power2.out"
      }, "-=0.4");
      document.querySelectorAll<SVGPathElement>(".ed-marker-path, .ed-divider-path").forEach(path => {
        const length = path.getTotalLength();
        path.style.strokeDasharray = `${length}`;
      });
    });
    return () => mm.revert();
  }, {
    scope: container
  });
  return <section data-section-id="23059" id="wiganmarketlandscapeanalysis" data-nav="light" ref={container} className="bg-background py-32 md:py-44 overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-10 md:gap-x-10">
          <div className="md:col-span-3">
            <div className="h-px w-full bg-border mb-6" />
            <p className="font-sans uppercase text-muted-foreground text-xs" style={{
            letterSpacing: "0.14em",
            fontWeight: 500
          }}>
              My Philosophy
            </p>
            <p className="mt-4 font-sans text-sm text-foreground">
              Direct from Yassine Essaadi
            </p>
          </div>

          <div className="md:col-span-9">
            <h2 className="font-serif text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.5vw, 3.7rem)",
            fontWeight: 400,
            letterSpacing: "-0.025em",
            lineHeight: 0.95
          }}>
              {headlineWords.map((word, i) => <span key={i} data-index={i} className="ed-word inline-block mr-[0.25em] relative">
                  <span className={word.italic ? "italic" : ""}>{word.text}</span>
                  {word.marked && <svg className="absolute left-0 right-0 pointer-events-none" style={{
                bottom: "-0.05em",
                width: "100%",
                height: "0.35em",
                overflow: "visible"
              }} viewBox="0 0 200 30" preserveAspectRatio="none" aria-hidden="true">
                      <path className="ed-marker-path" d="M4,20 C40,12 90,22 130,16 C160,12 180,18 196,14" fill="none" stroke="hsl(220 80% 30%)" strokeWidth="6" strokeLinecap="round" style={{
                  opacity: 0.9
                }} />
                    </svg>}
                </span>)}
            </h2>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-y-10 md:gap-x-10">
              <div className="md:col-span-6 ed-body">
                <p className="font-sans text-foreground" style={{
                fontSize: "clamp(1.25rem, 1.45vw, 1.55rem)",
                lineHeight: 1.55,
                fontWeight: 400,
                letterSpacing: "-0.005em"
              }}>
                  {"I founded YE Consultancy to provide a direct, expert-led SEO service. You work with me, not an account manager. My focus is on sustainable, data-backed strategies that deliver measurable business impact."}
                </p>
              </div>

              <div className="md:col-span-6 ed-signature-block flex flex-col items-start">
                <svg className="w-[180px] h-auto" viewBox="0 0 220 70" fill="none" aria-label="Signature of Yassine Essaadi">
                  <path d="M8,45 C14,20 22,18 26,38 C28,48 24,54 20,50 C18,48 22,42 30,40 C42,37 46,44 44,50 M50,42 C54,30 60,28 62,38 C63,44 58,48 54,44 C52,42 56,36 62,36 C70,36 72,42 70,46 M76,44 C80,32 86,30 88,40 C89,46 84,50 80,46 M94,42 C96,34 102,32 104,40 C105,44 102,48 98,44 M108,26 C108,44 112,52 118,44 C122,38 118,32 114,34 M124,42 C128,32 134,30 136,40 C137,44 134,48 130,44 M140,44 C144,32 150,28 154,36 C156,42 150,48 146,44 C144,42 148,36 154,36 C162,36 164,44 162,48 M170,30 C170,46 176,52 182,46 M188,42 C192,30 198,28 202,38 C204,42 200,48 196,44" stroke="hsl(240 9% 13%)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <path d="M6,58 C60,54 140,56 210,54" stroke="hsl(240 9% 13%)" strokeWidth="1" strokeLinecap="round" fill="none" opacity="0.4" />
                </svg>

                <p className="mt-4 font-sans text-foreground" style={{
                fontSize: "1rem",
                letterSpacing: "0.01em"
              }}>
                  {"Yassine Essaadi / SEO Consultant"}
                </p>

                <Button asChild variant="outline" className="mt-8 rounded-lg border-[1.5px] border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background px-8 py-6 h-auto font-sans text-sm transition-colors duration-300">
                  <Link to="/ServicesPage">View my services</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-24 md:mt-32 flex justify-center">
          <svg width="180" height="20" viewBox="0 0 180 20" fill="none" aria-hidden="true" style={{
          transform: "rotate(-8deg)"
        }}>
            <path className="ed-divider-path" d="M4,14 C40,8 90,16 130,10 C150,7 165,11 176,9" stroke="hsl(220 80% 30%)" strokeWidth="4" strokeLinecap="round" fill="none" style={{
            opacity: 0.9
          }} />
          </svg>
        </div>
      </div>
    </section>;
}